import { useState } from 'react'
import { Plus, Wallet } from 'lucide-react'
import { Link } from 'react-router'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { usePayrollRunMutations, usePayrollRuns } from '../api'
import { monthName, PAYROLL_STATUS_LABELS } from '../types'
import type { PayrollStatus } from '../types'
import { Select } from '@/components/ui/select'

interface LegalEntityOption { id: string; name: string }

const STATUS_TONE: Record<PayrollStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  OPEN: 'neutral',
  CALCULATED: 'info',
  LOCKED: 'warning',
  APPROVED: 'info',
  PAID: 'success',
  CLOSED: 'neutral',
}

/** Every payroll period, and where each one has got to. */
export function PayrollRunsPage() {
  useBreadcrumb()
  const runs = usePayrollRuns()
  const entities = useLookup<LegalEntityOption>('legalEntities')
  const { open } = usePayrollRunMutations()

  const now = new Date()
  const [draft, setDraft] = useState<{
    legalEntityId: string; year: string; month: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onOpen() {
    if (!draft) return
    setError(null)
    try {
      await open.mutateAsync({
        legalEntityId: draft.legalEntityId,
        year: Number(draft.year),
        month: Number(draft.month),
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That run could not be opened')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payroll"
        subtitle="One run per entity per month, from opening it to closing it."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                legalEntityId: entities.data?.[0]?.id ?? '',
                year: String(now.getFullYear()),
                month: String(now.getMonth() + 1),
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Open a run
          </Button>
        }
      />

      <Card>
        {runs.isPending && <Skeleton className="m-4 h-24" />}
        {runs.isError && <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />}
        {runs.isSuccess && runs.data.length === 0 && (
          <EmptyState
            title="No payroll has been run"
            description="Open a run for a month, calculate it, and review the figures before anybody approves them."
          />
        )}
        {runs.isSuccess && runs.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Period</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium">People</th>
                  <th className="p-3 text-end font-medium">Gross</th>
                  <th className="p-3 text-end font-medium">Net</th>
                  <th className="p-3 text-start font-medium">Reopened</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.map((run) => (
                  <tr key={run.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <Link className="underline underline-offset-2" to={`/payroll/runs/${run.id}`}>
                        {monthName(run.periodYear, run.periodMonth)}
                      </Link>
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={run.status}
                        tone={STATUS_TONE[run.status]}
                        label={PAYROLL_STATUS_LABELS[run.status]}
                      />
                    </td>
                    <td className="p-3 text-end">{run.employeeCount}</td>
                    <td className="p-3 text-end">{run.totalGross}</td>
                    <td className="p-3 text-end">{run.totalNet}</td>
                    <td className="p-3">
                      {run.reopenCount === 0
                        ? <span className="text-slate-500">never</span>
                        : `${run.reopenCount} time${run.reopenCount === 1 ? '' : 's'}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Wallet className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Preparing a run and approving it are separate permissions, and the same person cannot
        do both — releasing payment is a third step again.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(nextOpen) => { if (!nextOpen) setDraft(null) }}
        title="Open a payroll run"
        description="One run per entity per month. Opening it does not calculate anything yet."
        submitLabel="Open it"
        loading={open.isPending}
        error={error}
        onSubmit={() => void onOpen()}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="run-entity">Legal entity</Label>
              <Select
                id="run-entity"
                className="w-full"
                value={draft.legalEntityId}
                onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
              >
                <option value="">Choose an entity…</option>
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="run-year">Year</Label>
                <Input
                  id="run-year"
                  type="number"
                  value={draft.year}
                  onChange={(event) => setDraft({ ...draft, year: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="run-month">Month</Label>
                <Select
                  id="run-month"
                  className="w-full"
                  value={draft.month}
                  onChange={(event) => setDraft({ ...draft, month: event.target.value })}
                >
                  {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                    <option key={month} value={month}>
                      {new Date(2000, month - 1, 1).toLocaleString('en', { month: 'long' })}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
